/**
 * PE Diligence TUI Web - Main Application
 */

import { useState, useCallback, useEffect } from 'react'
import { MagicLinkLogin } from './components/MagicLinkLogin'
import { ChallengeMode } from './components/ChallengeMode'
import { Header } from './components/layout/Header'
import { TabNav } from './components/layout/TabNav'
import { StatusBar } from './components/layout/StatusBar'
import { FinancialsPanel } from './components/panels/FinancialsPanel'
import { FCFPanel } from './components/panels/FCFPanel'
import { RegimePanel } from './components/panels/RegimePanel'
import { ChatPanel } from './components/panels/ChatPanel'
import { MAPanel } from './components/panels/MAPanel'
import { DDReportPanel } from './components/panels/DDReportPanel'
import { DataRoomPanel } from './components/panels/DataRoomPanel'
import { HistoryPanel } from './components/panels/HistoryPanel'
import { CompanySelectModal, type CompanyMatch, type PrivateCompanyMetadata } from './components/modals/CompanySelectModal'
import { DEFAULT_TABS, type CompanyData, type StatusState } from './types'
import { getCompany, getFinancials, getRegime, checkHealth, searchCompanies, type ChatContext, type FinancialData, type RegimeData } from './services/api'

export function App() {
  const [authenticated, setAuthenticated] = useState(false)
  const [activeTab, setActiveTab] = useState('challenge')
  const [company, setCompany] = useState<CompanyData | null>(null)
  const [searchValue, setSearchValue] = useState('')
  const [isSearchFocused, setIsSearchFocused] = useState(true)
  const [status, setStatus] = useState<StatusState>({
    message: 'Connecting to API...',
    type: 'loading',
  })
  const [_apiReady, setApiReady] = useState(false)

  // Company selection modal state
  const [showCompanyModal, setShowCompanyModal] = useState(false)
  const [companyMatches, setCompanyMatches] = useState<CompanyMatch[]>([])
  const [pendingSearchQuery, setPendingSearchQuery] = useState('')
  const [isSearching, setIsSearching] = useState(false)

  // Additional state for chat context
  const [latestFinancials, setLatestFinancials] = useState<FinancialData | null>(null)
  const [regimeData, setRegimeData] = useState<RegimeData | null>(null)

  // Check API health on startup
  useEffect(() => {
    const init = async () => {
      try {
        const result = await checkHealth()
        if (result.data) {
          setApiReady(true)
          setStatus({
            message: 'Ready - Enter a ticker symbol to analyze',
            type: 'info',
          })
        } else {
          setStatus({
            message: 'API unavailable - using local mode',
            type: 'warning',
          })
        }
      } catch {
        setStatus({
          message: 'API unavailable - using local mode',
          type: 'warning',
        })
      }
    }
    init()
  }, [])

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Quit on Ctrl+Q or Escape
      if ((e.key === 'q' && e.ctrlKey) || e.key === 'Escape') {
        if (!isSearchFocused) {
          setIsSearchFocused(true)
        }
        return
      }

      // Tab switching with number keys (when not in input)
      if (!isSearchFocused && e.key >= '1' && e.key <= '8') {
        const tabIndex = parseInt(e.key) - 1
        if (tabIndex < DEFAULT_TABS.length) {
          setActiveTab(DEFAULT_TABS[tabIndex].id)
        }
      }

      // Focus search with /
      if (e.key === '/' && !isSearchFocused) {
        e.preventDefault()
        setIsSearchFocused(true)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isSearchFocused])

  // Handle search/analyze
  const handleSearch = useCallback((value: string) => {
    setSearchValue(value)
  }, [])

  // Handle selecting a public company from modal
  const handleSelectPublicCompany = useCallback(async (match: CompanyMatch) => {
    setShowCompanyModal(false)
    setStatus({ message: `Loading ${match.name}...`, type: 'loading' })

    try {
      const result = await getCompany(match.ticker)

      if (result.error) {
        // Use search result data as fallback
        setCompany({
          ticker: match.ticker,
          name: match.name,
          sector: match.sector || 'Unknown',
          industry: 'Unknown',
          marketCap: 0,
          description: `Company data for ${match.name}`,
          quoteType: match.quoteType,
        })
      } else if (result.data) {
        setCompany({
          ticker: result.data.ticker,
          name: result.data.name,
          sector: result.data.sector,
          industry: result.data.industry,
          marketCap: result.data.marketCap,
          description: result.data.description,
          quoteType: match.quoteType,
        })
      }

      setSearchValue(match.ticker)
      setStatus({ message: `Loaded ${match.name} (${match.ticker})`, type: 'success' })
      setActiveTab('financials')
      setIsSearchFocused(false)
    } catch (error) {
      console.error('Error loading company:', error)
      setStatus({ message: `Error loading ${match.name}`, type: 'error' })
    }
  }, [])

  // Handle selecting private company from modal
  const handleSelectPrivateCompany = useCallback((metadata: PrivateCompanyMetadata) => {
    setShowCompanyModal(false)
    const query = pendingSearchQuery

    // Create a sanitized ticker from the company name
    const sanitizedTicker = query
      .toUpperCase()
      .replace(/[^A-Z0-9\s]/g, '')
      .replace(/\s+/g, '-')
      .substring(0, 20)

    setCompany({
      ticker: sanitizedTicker,
      name: query,
      sector: metadata.sector || 'Private',
      industry: 'Unknown',
      marketCap: 0,
      description: `Private company: ${query}. ${metadata.hqLocation ? `HQ: ${metadata.hqLocation}. ` : ''}${metadata.estimatedSize ? `Size: ${metadata.estimatedSize}. ` : ''}${metadata.estimatedRevenue ? `Revenue: ${metadata.estimatedRevenue}.` : ''}`,
      quoteType: 'PRIVATE',
      isPrivate: true,
      // Store metadata for enhanced research
      privateMetadata: metadata,
    })

    setLatestFinancials(null)
    setRegimeData(null)

    setStatus({
      message: `Loaded ${query} as private company - use Data Room for document collection`,
      type: 'success',
    })

    setActiveTab('financials')
    setIsSearchFocused(false)
  }, [pendingSearchQuery])

  const handleAnalyze = useCallback(async () => {
    if (!searchValue.trim()) {
      setStatus({ message: 'Please enter a company name or ticker', type: 'warning' })
      return
    }

    const query = searchValue.trim()
    setPendingSearchQuery(query)

    // Search for public companies first, show modal for selection
    setStatus({ message: `Searching for "${query}"...`, type: 'loading' })
    setIsSearching(true)

    try {
      const searchResult = await searchCompanies(query)

      if (searchResult.error) {
        // Search failed - show modal with no matches (prompts private company flow)
        setCompanyMatches([])
        setShowCompanyModal(true)
        setIsSearching(false)
        setStatus({ message: `No public companies found for "${query}"`, type: 'info' })
        return
      }

      if (searchResult.data) {
        const { matches } = searchResult.data

        // Convert to CompanyMatch format
        const companyMatchList: CompanyMatch[] = matches.map((m: { ticker: string; name: string; sector?: string; exchange?: string; quoteType?: string }) => ({
          ticker: m.ticker,
          name: m.name,
          sector: m.sector,
          exchange: m.exchange,
          quoteType: m.quoteType,
        }))

        setCompanyMatches(companyMatchList)
        setShowCompanyModal(true)
        setIsSearching(false)

        if (matches.length === 0) {
          setStatus({ message: `No public companies found for "${query}"`, type: 'info' })
        } else {
          setStatus({ message: `Found ${matches.length} matching ${matches.length === 1 ? 'company' : 'companies'}`, type: 'info' })
        }
      }
    } catch (error) {
      console.error('Analysis error:', error)
      setIsSearching(false)
      // On error, show modal with no matches
      setCompanyMatches([])
      setShowCompanyModal(true)
      setStatus({ message: `Search error - select company type`, type: 'warning' })
    }
  }, [searchValue])

  // Handle tab change
  const handleTabChange = useCallback((tabId: string) => {
    setActiveTab(tabId)
    setIsSearchFocused(false)
  }, [])

  // Handle selecting a company from history
  const handleSelectCompany = useCallback((ticker: string, companyName: string) => {
    setCompany({
      ticker,
      name: companyName,
      sector: 'Technology',
      industry: 'Software',
      marketCap: 1000000000,
      description: `Company data for ${ticker}`,
    })
    setSearchValue(ticker)
    setActiveTab('financials')
    setStatus({ message: `Loaded ${ticker} from history`, type: 'success' })
  }, [])

  // Handle re-analyze from history
  const handleReanalyze = useCallback(async (ticker: string) => {
    setSearchValue(ticker)
    setStatus({ message: `Re-analyzing ${ticker}...`, type: 'loading' })

    try {
      const result = await getCompany(ticker)

      if (result.error) {
        setCompany({
          ticker,
          name: `${ticker} Corporation`,
          sector: 'Technology',
          industry: 'Software',
          marketCap: 1000000000,
          description: `Company data for ${ticker}`,
        })
        setStatus({ message: `Re-analyzed ${ticker} (local mode)`, type: 'success' })
      } else if (result.data) {
        setCompany({
          ticker: result.data.ticker,
          name: result.data.name,
          sector: result.data.sector,
          industry: result.data.industry,
          marketCap: result.data.marketCap,
          description: result.data.description,
        })
        setStatus({ message: `Re-analyzed ${ticker}`, type: 'success' })
      }

      setActiveTab('financials')
      setIsSearchFocused(false)
    } catch (error) {
      console.error('Re-analysis error:', error)
      setStatus({ message: `Error re-analyzing ${ticker}`, type: 'error' })
    }
  }, [])

  // Fetch additional context data when company changes
  // Skip for private companies since they don't have public financial data
  useEffect(() => {
    const fetchContextData = async () => {
      if (!company?.ticker) return

      // Skip API calls for private companies
      if (company.isPrivate) {
        setLatestFinancials(null)
        setRegimeData(null)
        return
      }

      try {
        // Fetch financials and regime in parallel
        const [financialsResult, regimeResult] = await Promise.all([
          getFinancials(company.ticker),
          getRegime(company.ticker),
        ])

        if (financialsResult.data) {
          setLatestFinancials(financialsResult.data)
        }

        if (regimeResult.data) {
          setRegimeData(regimeResult.data)
        }
      } catch (error) {
        console.log('Could not fetch context data:', error)
      }
    }

    fetchContextData()
  }, [company?.ticker, company?.isPrivate])

  // Build chat context from company, financials, and regime data
  const buildChatContext = useCallback((): ChatContext => {
    return {
      ticker: company?.ticker,
      name: company?.name,
      sector: company?.sector,
      industry: company?.industry,
      marketCap: company?.marketCap,
      revenue: latestFinancials?.revenue,
      fcf: latestFinancials?.freeCashFlow,
      regime: regimeData?.currentRegime,
    }
  }, [company, latestFinancials, regimeData])

  // Handle chat message (status update callback from ChatPanel)
  const handleSendChatMessage = useCallback((message: string) => {
    setStatus({ message: `Sending: ${message.substring(0, 30)}...`, type: 'loading' })
    // The actual API call is now handled by ChatPanel
    // This callback is just for status updates
    setTimeout(() => {
      setStatus({ message: 'Message sent to AI assistant', type: 'success' })
    }, 500)
  }, [])

  // Render active panel
  const renderPanel = () => {
    const panelProps = { company }

    switch (activeTab) {
      case 'challenge':
        return <ChallengeMode />
      case 'financials':
        return <FinancialsPanel {...panelProps} />
      case 'fcf':
        return <FCFPanel {...panelProps} />
      case 'regime':
        return <RegimePanel {...panelProps} />
      case 'chat':
        return <ChatPanel {...panelProps} context={buildChatContext()} onSendMessage={handleSendChatMessage} />
      case 'ma':
        return <MAPanel {...panelProps} />
      case 'report':
        return <DDReportPanel {...panelProps} />
      case 'dataroom':
        return <DataRoomPanel {...panelProps} />
      case 'history':
        return <HistoryPanel onSelectCompany={handleSelectCompany} onReanalyze={handleReanalyze} />
      default:
        return (
          <div className="flex items-center justify-center h-full text-term-textDim">
            {company
              ? `${activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} panel for ${company.ticker}`
              : 'Enter a ticker symbol above to begin analysis'}
          </div>
        )
    }
  }

  // Show login if not authenticated
  if (!authenticated) {
    return <MagicLinkLogin onAuthenticated={() => setAuthenticated(true)} />
  }

  return (
    <div className="h-screen flex flex-col bg-term-bgDark">
      {/* Header with search */}
      <Header
        company={company}
        searchValue={searchValue}
        onSearchChange={handleSearch}
        onAnalyze={handleAnalyze}
        isSearchFocused={isSearchFocused}
        onSearchFocus={() => setIsSearchFocused(true)}
        onSearchBlur={() => setIsSearchFocused(false)}
      />

      {/* Tab Navigation */}
      <TabNav
        tabs={DEFAULT_TABS}
        activeTab={activeTab}
        onTabChange={handleTabChange}
      />

      {/* Main Content Area */}
      <main className="flex-1 overflow-hidden p-4">
        <div className="h-full term-box p-4 overflow-auto">
          {renderPanel()}
        </div>
      </main>

      {/* Status Bar */}
      <StatusBar message={status.message} type={status.type} />

      {/* Company Selection Modal */}
      {showCompanyModal && (
        <CompanySelectModal
          searchQuery={pendingSearchQuery}
          matches={companyMatches}
          onSelectPublic={handleSelectPublicCompany}
          onSelectPrivate={handleSelectPrivateCompany}
          onCancel={() => setShowCompanyModal(false)}
          isSearching={isSearching}
        />
      )}
    </div>
  )
}
